import React, {Component, Fragment} from 'react';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers/persistedReducer';
import { doBlinkCell } from '../store/actions/cellDecorators';
import { doUnfocusCell, doSetLayoutUpdateAvailability, doUpdateLayoutAndCells, doStopPanningToCell, doSetLayoutLoaded } from '../store/actions/channelLayout';
import { selectLayoutData, selectCellLayoutById, selectCurrentChannel, selectLayoutUpdateAvailable, selectChannelPopup, selectMobileMenuOpen, selectLayoutType, selectLayoutColumnDims, selectDesktopDrawerOpen, selectIsUserGuest } from '../store/selectors';

import ChannelGrid from './ChannelGrid'
import ChannelMapInteraction from './ChannelMapInteraction';
import Compass from './Compass';

import { GoSettings } from 'react-icons/go'
import { IoMdRefresh, IoMdMenu } from "react-icons/io";
import { TiArrowLeftThick, TiArrowRightThick } from "react-icons/ti";
import { ReactComponent as FavoritesIcon } from '../images/favorites.svg'

import { ReactComponent as SearchIcon } from '../images/search.svg'
import { ReactComponent as Logo } from '../images/cluster-logo.svg'

import store from '../store/store';

import { doOpenModal } from '../store/actions/modal';
import { ModalType } from '../Modal/ModalTypes';
import ChannelPopup from './ChannelPopup';

import styles from './Channel.module.scss';
import { doSetMobileMenuOpen } from '../store/actions/system';
import ChannelFilters from './ChannelFilters';
import { CellLayoutType, LAYOUT_COLUMN_GAP, LAYOUT_OUTER_SPACING } from './ChannelDataTypes';
import classnames from 'classnames';
import { CellDataTypes } from '@cluster/web-components';
import { throttle } from 'throttle-debounce';

type ChannelState = {
  scale: number,
  compassAngle: number,
  compassHidden: boolean,
  translation: {
    x: number,
    y: number
  },
  translationBounds: {
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number
  },
  translationBoundsOverride: {
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number
  },
  animateTransition: boolean,
  canColumnPan: boolean,
  currentColumn: number
}

class Channel extends Component<PropsFromRedux, ChannelState> {
  channelWrapper: HTMLDivElement | null;

  constructor(props: PropsFromRedux) {
    super(props)

    this.channelWrapper = null;

    this.state = {
      scale: 1,
      compassAngle: 0,
      compassHidden: true,
      translation: {
        x: 0,
        y: 0
      },
      translationBounds: {
        xMin: 0,
        xMax: 0,
        yMin: 0,
        yMax: 0
      },
      translationBoundsOverride: {
        xMin: 0,
        xMax: 0,
        yMin: 0,
        yMax: 0
      },
      animateTransition: false,
      canColumnPan: false,
      currentColumn: 0
    }
  }

  componentDidUpdate (prevProps: PropsFromRedux) {
    // Reasons to reset canvas sizing.
    if (prevProps.channelId !== this.props.channelId ||
    prevProps.channelWidth !== this.props.channelWidth ||
    prevProps.channelHeight !== this.props.channelHeight ||
    prevProps.windowWidth !== this.props.windowWidth ||
    prevProps.desktopDrawerOpen !== this.props.desktopDrawerOpen) {
      const canvasScale = prevProps.channelId !== this.props.channelId ? 1 : this.state.scale
      this.setChannelCanvas(this.props.channelWidth, this.props.channelHeight, canvasScale);
      this.setCanColumnPan();
    }
    // If the channel did not need a new canvas, check for a channel update
    // by looking at the layoutLoading transition from true to false and
    // whether there was an error. Then set loaded.
    else if (prevProps.layoutLoading === true && this.props.layoutLoading === false && this.props.layoutLoaded === false && this.props.layoutError === null) {
      this.props.doSetLayoutLoaded(true);
    }

    // If a cell is focused, make sure the cell is fully in the viewport.
    if (this.props.focusedCell !== false && this.props.focusedCell !== prevProps.focusedCell) {
      this.fullyExposeCell(this.props.focusedCell);
    } else if (prevProps.focusedCell !== false && this.props.focusedCell === false) {
      // If a cell comes out of focus, unset any viewport overrides.
      this.overrideTranslationBounds(false);
    }

    // If there is a panned cell, and the layout is not loading, and has completed loading, pan to the cell.
    if (this.props.pannedCell && this.props.layoutLoaded && !this.props.layoutLoading) {
      this.panToCell(this.props.pannedCell);
      this.props.doStopPanningToCell();
    }
  }

  getChannelViewportDimensions = () => {
    let dims = {
      w: this.channelWrapper?.clientWidth??0,
      h: this.channelWrapper?.clientHeight??0
    }

    // Restrict canvas to channel width on large desktops for column types
    if (this.props.layoutType === "columns") {
      dims.w = Math.min(dims.w, (this.props.channelWidth + 30));
    }

    return dims;
  }

  // Helper to check if a translation bounds type is non-zero.
  checkBoundsNonZero = (bounds: ChannelState["translationBoundsOverride"]) => {
    return bounds.xMax !== 0 || bounds.xMin !== 0 || bounds.yMax !== 0 || bounds.yMin !== 0;
  }

  // Allows the channel translation bounds to be overridden. Passing false will remove override.
  overrideTranslationBounds = (override: ChannelState["translationBoundsOverride"] | false, callback?: () => void) => {
    let bounds = this.state.translationBounds;
    let translation = this.state.translation;

    // Remove override
    if (override === false) {
      if (this.checkBoundsNonZero(this.state.translationBoundsOverride)) {
        // Subtracts any override values from current bounds values.
        bounds.xMax -= this.state.translationBoundsOverride.xMax;
        bounds.xMin -= this.state.translationBoundsOverride.xMin;
        bounds.yMax -= this.state.translationBoundsOverride.yMax;
        bounds.yMin -= this.state.translationBoundsOverride.yMin;

        // If removing the override puts the current translation out of the original bounds,
        // set the translation to equal the bounds.
        if (translation.y > bounds.yMax) {
          translation.y = bounds.yMax;
        }
        if (translation.y < bounds.yMin) {
          translation.y = bounds.yMin;
        }
        if (translation.x > bounds.xMax) {
          translation.x = bounds.xMax;
        }
        if (translation.x < bounds.xMin) {
          translation.x = bounds.xMin;
        }

        this.setState({
          translationBoundsOverride: {xMax: 0, xMin: 0, yMax: 0, yMin: 0},
          translationBounds: bounds,
          translation
        }, callback);

      } else {
        if (callback !== undefined) {
          callback();
        }
      }
    }
    // Add override
    else {
      if (this.checkBoundsNonZero(override)) {
        bounds.xMax += override.xMax;
        bounds.xMin += override.xMin;
        bounds.yMax += override.yMax;
        bounds.yMin += override.yMin;

        this.setState({
          translationBoundsOverride: override,
          translationBounds: bounds
        }, callback);

      } else {
        if (callback !== undefined) {
          callback();
        }
      }
    }
  }

  // Calculates the channel scroll bounds based on channel size and window viewport
  getTranslationBounds(chanDims: { w: number, h: number }, chanVpDims: { w: number, h: number }, scale: number) {
    // Get scaled channel size
    const scaledChanDims = {
      w: chanDims.w * scale,
      h: chanDims.h * scale
    }

    // A max bound of 0 puts the top left of the channel in the top left corner of the viewport.
    let xMaxBound = 0;
    let yMaxBound = 0;

    // Min bound needs to put the bottom right corner of the channel into the bottom right corner of the viewport.
    let xMinBound = -(scaledChanDims.w - chanVpDims.w);
    let yMinBound = -(scaledChanDims.h - chanVpDims.h);

    // If the channel width is smaller than the viewport, it doesn't need to move left any,
    // but needs to move right in the amount of the difference between viewport and channel width to be able to center it.
    if (scaledChanDims.w < chanVpDims.w) {
      xMinBound = 0;
      xMaxBound = chanVpDims.w - scaledChanDims.w;
    }

    // If the channel height is smaller than the viewport, it doesn't need to move up any,
    // but needs to move down in the amount of the difference between viewport and channel height to be able to center it.
    if (scaledChanDims.h < chanVpDims.h) {
      yMinBound = 0;
      yMaxBound = chanVpDims.h - scaledChanDims.h;
    }

    return {
      xMin: xMinBound,
      xMax: xMaxBound,
      yMin: yMinBound,
      yMax: yMaxBound
    }
  }

  setChannelCanvas = (w: number, h: number, scale: number) => {
    // Get translation bounds, adding outer spacing to the channel size.
    const translationBounds = this.getTranslationBounds(
      {
        w: w+LAYOUT_OUTER_SPACING*2,
        h: h+LAYOUT_OUTER_SPACING*2
      },
      this.getChannelViewportDimensions(),
      scale
    );

    this.setState({
      animateTransition: false,
      compassAngle: 0,
      translationBounds,
      scale
    }, () => {
      const centroidCenter = this.getHomeCenter();
      const currentColumn = this.getClosestColumn(centroidCenter.x);
      this.setState({
        translation: {x: centroidCenter.x, y: centroidCenter.y},
        currentColumn
      })
    });

    // Notify the state that the layout is loaded. This doesn't mean cells exist yet.
    this.props.doSetLayoutLoaded(true);
  }

  moveCanvas = ({ scale, translation }: any, animate = false) => {
    this.setCompassThrottled(scale, translation)
    // Calculate new bounds if the scale changes.
    let translationBounds = this.state.translationBounds;
    if (scale !== this.state.scale) {
      translationBounds = this.getTranslationBounds(
        {
          w: this.props.channelWidth,
          h: this.props.channelHeight
        },
        this.getChannelViewportDimensions(),
        scale
      );
    }

    this.setState({
      scale,
      translation,
      animateTransition: animate,
      translationBounds
     })
  }

  setCompass = (scale: number, translation: {x: number, y: number}) => {
    const centroidLayout = this.getCentroidLayout();

    if (centroidLayout) {
      const centroidCenter = this.calcCellCenter(centroidLayout);

      const compassAngle = (Math.atan2(centroidCenter.y - translation.y, centroidCenter.x - translation.x) * 180 / Math.PI);

      // Calculate centroid distance from center.
      const distance = {
        x: centroidCenter.x - translation.x,
        y: centroidCenter.y - translation.y
      }

      const chanVpDims = this.getChannelViewportDimensions();

      const compassHidden = (Math.abs(distance.x) + scale*centroidLayout.width/2) < chanVpDims.w/2
                          && (Math.abs(distance.y) + scale*centroidLayout.height/2) < chanVpDims.h/2

      this.setState({
        compassAngle,
        compassHidden,
      })
    }
  }

  setCompassThrottled = throttle(40, (scale: number, translation: {x: number, y: number}) => this.setCompass(scale, translation))

  getCellLayout = (cellId: number) => {
    const state = store.getState();

    return selectCellLayoutById(state, cellId);
  }

  getCentroidLayout = () => {
    return this.getCellLayout(this.props.centroidId);
  }

  calcCellCenter = (layout: CellLayoutType) => {
    // cellLayout x/y are the top/left sides of the cell. Add that to half of the window dimensions places the
    // top right corner of the cell in the middle of the screen. Then add half the width/height to fully center.
    // we also multiply the current canvas scale to the canvas positioning data to get physical panning amounts.

    const chanVpDims = this.getChannelViewportDimensions();

    return {
      x: chanVpDims.w/2 - this.state.scale*(layout.width/2 + LAYOUT_OUTER_SPACING + layout.x),
      y: chanVpDims.h/2 - this.state.scale*(layout.height/2 + LAYOUT_OUTER_SPACING + layout.y)
    }
  }

  getCellCenter = (cellId: number) => {
    const cellLayout = this.getCellLayout(cellId);

    if (cellLayout) {
      const cellCenter = this.calcCellCenter(cellLayout);

      if (this.props.layoutType === "columns") {
        return {
          x: this.getColumnCenter(cellLayout.column!),
          y: cellCenter.y
        }
      }

      else return cellCenter;
    }
  }

  getHomeCenter = () => {
    if (this.props.layoutType === "columns") {
      const centroidLayout = this.getCentroidLayout();
      if (typeof centroidLayout !== "undefined") {
        return {
          x: this.getColumnCenter(centroidLayout.column!),
          y: 0
        }
      }
    }

    return this.getCellCenter(this.props.centroidId) ?? {x: 0, y: 0};
  }

  panToHome = (animate = false) => {
    const centroidCenter = this.getHomeCenter();

    if (centroidCenter) {
      this.setState({
        animateTransition: animate,
        translation: {x: centroidCenter.x, y: centroidCenter.y},
        compassHidden: true
      });
    }
  }

  panToCell = (cellId: number) => {
    const cellCenter = this.getCellCenter(cellId);

    if (cellCenter) {

      // Unfocus a cell if one is focused while we pan.
      if (this.props.focusedCell !== false) {
        this.props.doUnfocusCell();
      }

      this.moveCanvas({
        scale: this.state.scale,
        translation: {
          x: cellCenter.x,
          y: cellCenter.y
        },
      }, true);

      // Blink Cell
      this.props.doBlinkCell(cellId)
    }
  }

  // If the width + column gap of any column is smaller than half the viewport,
  // the layout cannot have column panning.
  setCanColumnPan = () => {
    let canColumnPan = false;
    const columnWidths = this.props.layoutColumnDims.map((dims) => dims.width) ?? [];

    if (columnWidths.length > 0) {
      const chanVpDims = this.getChannelViewportDimensions();

      canColumnPan = true;
      for (let i=0; i<columnWidths.length; i++) {
        if (columnWidths[i] < chanVpDims.w/2) {
          canColumnPan = false;
        }
      }
    }
    this.setState({ canColumnPan });
  }

  getColumnCenter = (columnNum: number) => {
    const chanVpDims = this.getChannelViewportDimensions();
    const columnWidths = this.props.layoutColumnDims.map((dims) => dims.width) ;

    if (columnWidths === []) {
      return 0;
    }

    let columnCenter = 0;

    for (let i=0; i<=columnNum; i++) {
      if (i !== columnNum) {
        columnCenter += columnWidths[i] + LAYOUT_COLUMN_GAP + CellDataTypes.CELL_GAP*2;
      } else {
        columnCenter += columnWidths[i]/2;
      }
    }

    return chanVpDims.w/2 - this.state.scale*(columnCenter) - LAYOUT_OUTER_SPACING;
  }

  panToColumn = (columnNum: number) => {
    if (!this.state.canColumnPan) {
      return false;
    }

    this.setState((state) => ({
      animateTransition: true,
      translation: {x: this.getColumnCenter(columnNum), y: state.translation.y},
      compassHidden: true,
      currentColumn: columnNum
    }));
  }

  calcColumnCenters = () => this.props.layoutColumnDims?.map((dims, columnNum) => this.getColumnCenter(columnNum)) ?? [];

  getClosestColumn = (x: number) => {
    const columnCenters = this.calcColumnCenters();
    let distances = columnCenters.map((center, index) => {return {column: index, dist: Math.abs(center-x)}}).sort((a, b) => {
      if (a.dist > b.dist) return 1;
      if (b.dist > a.dist) return -1;

      return 0;
    })

    return distances.length>0 ? distances[0].column : 0;
  }

  // Make a cell fully visible in the viewport, including padding for the cell tags and menu.
  fullyExposeCell = (cellId: number) => {
    const cellPos = this.getCellLayout(cellId);
    // Channel Viewport Dimensions.
    const chanVpDims = this.getChannelViewportDimensions();

    if (cellPos) {
      // Shift amounts in px.
      let outBounds = {
        x: 0,
        y: 0
      };

      // Pull values out of state.
      const { scale, translation } = this.state;

      // The amount of padding from the edge of the canvas to expose.
      const yPadding = 100;
      const xPadding = 40;

      // Holds bounds override values
      let override = {
        xMax: 0,
        xMin: 0,
        yMax: 0,
        yMin: 0
      };

      // cellPos x/y are the top/left sides of the cell. Add the width/height to get the bottom/right sides.
      // Adding the current canvas translation (which is negative) gives you the relative positioning of the cell on the screen.
      // We also add/subtract padding from the edges to make way for focused cell off canvas items.
      // We also multiply the current canvas scale to the canvas positioning data to get physical panning amounts.

      if (scale*(cellPos.x + cellPos.width + xPadding) + translation.x > chanVpDims.w) {
        // If right edge of cell greater than channel width.
        outBounds.x = scale*(cellPos.x + cellPos.width + xPadding) + translation.x - chanVpDims.w;
        override.xMin = -xPadding;
      } else if (scale*(cellPos.x - xPadding) + translation.x < 0) {
        // If left edge of cell less than 0.
        outBounds.x = scale*(cellPos.x - xPadding) + translation.x;
        override.xMax = xPadding;
      }

      if (scale*(cellPos.y + cellPos.height + yPadding) + translation.y > chanVpDims.h) {
        // If bottom edge of cell greater than channel height.
        outBounds.y = scale*(cellPos.y + cellPos.height + yPadding) + translation.y - chanVpDims.h;
        override.yMin = -yPadding;
      } else if (scale*(cellPos.y- yPadding) + translation.y  < 0) {
        // If top edge of cell less than 0.
        outBounds.y = scale*(cellPos.y - yPadding) + translation.y;
        override.yMax = yPadding;
      }

      if (outBounds.x !== 0 || outBounds.y !== 0) {
        this.overrideTranslationBounds(override, () => {
          // Pan the amount out of bounds.
          const xPan = translation.x - outBounds.x;
          const yPan = translation.y - outBounds.y;

          this.moveCanvas({
            scale,
            translation: {
              x: xPan,
              y: yPan
            },
          }, true);
        });
      }
    }
  }

  render() {
    const { scale, translation, translationBounds, animateTransition } = this.state;

    return (
      <div className={classnames(styles.channel, [styles['channel_' + this.props.theme]], {[styles.mobileDrawerOpen]: this.props.mobileMenuOpen})}>
        <div className={this.props.mobileMenuOpen ? styles.channelMobileOverlayActive : styles.channelMobileOverlay} onClick={(e) => this.props.doSetMobileMenuOpen(false)}></div>
        <ChannelTitleBar title={this.props.channelName} />

        { this.props.layoutLoading || this.props.layoutError ?
          <ChannelStatus error={this.props.layoutError} />
        :
          this.props.channelId
          ?
            <Fragment>
              <Compass angle={this.state.compassAngle} hidden={this.state.compassHidden || this.props.layoutType === "columns"} handleClick={() => this.panToHome(true)} />
              <div className={styles.channelWrapper} ref={(channelWrapper) => { this.channelWrapper = channelWrapper }}>
                <ChannelMapInteraction
                  value={{scale, translation}}
                  onChange={this.moveCanvas}
                  minScale={0.5}
                  maxScale={1}
                  translationBounds={translationBounds}
                  animateTransition={animateTransition}
                  transitionAnimationTime={300}
                  disableZoom={this.props.focusedCell !== false || this.props.layoutType === "columns"}
                  disablePan={this.props.focusedCell !== false}
                  disableInertialPanning={this.props.layoutType === "columns" && this.state.canColumnPan}
                  onStoppedMoving={() => {
                    if (this.state.canColumnPan) {
                      this.panToColumn(this.getClosestColumn(this.state.translation.x))
                    }
                  }}
                  wheelBehavior={this.props.layoutType === "columns" ? "y-scroll" : 'zoom'}
                >
                  <ChannelGrid />
                </ChannelMapInteraction>
              </div>
              <ChannelPopup />
              <ChannelColumnControl visible={this.state.canColumnPan} numCols={this.props.layoutColumnDims.length} curCol={this.state.currentColumn} panToCol={this.panToColumn} />
            </Fragment>
          :
            <div className={styles.noChannel}>
              <Logo className={styles.noChannelLogo} />
              <div className={styles.noChannelTitle}>Select a Channel to get started</div>
            </div>
        }

        {
          // Show filters and menu bar if there is an active channel, even if there is an error or it's loading.
          this.props.channelId ?
          <Fragment>
            {this.props.layoutType !== "columns" ? <ChannelFilters /> : ''}
            <ChannelMenuBar />
          </Fragment> : ''
        }

      </div>
    );
  }
}

function ChannelStatus({error}: { error?: string | null}) {
  const isError = error !== null && typeof error !== "undefined" && error !== "";
  return (
    <div className={isError ? styles.channelError : styles.channelStatus}>
      { isError ? error : "Loading..."}
    </div>
  )
}

function ChannelTitleBar({title}: {title?: string}) {
  const dispatch = useDispatch();
  const updateAvailable = useSelector(selectLayoutUpdateAvailable);
  const channelPopup = useSelector(selectChannelPopup);
  const isMobile = useSelector((state: RootState) => state.system.isMobile);

  // Don't show the reload button while the prompt is up. Because it was weird.
  const showReloadButton = updateAvailable && channelPopup !== "updatePrompt";

  const updateChannel = (e: React.MouseEvent) => {
    dispatch(doUpdateLayoutAndCells());
    dispatch(doSetLayoutUpdateAvailability(false));
  }

  const openMobileMenu = (e: React.MouseEvent) => {
    dispatch(doSetMobileMenuOpen(true));
  }

  return (
    <div className={styles.channelTitleBar}>
      { isMobile && <button onClick={openMobileMenu} className={styles.mobileMenuButton}><IoMdMenu size="40px" /></button> }
      <div className={styles.channelTitleBarTitle}>{title || "Channel"}</div>
      { showReloadButton && <button onClick={updateChannel} className={styles.reloadButton}><IoMdRefresh size="20px" />Reload</button>}
    </div>
  )
}

function ChannelMenuBar() {
  const dispatch = useDispatch();
  const userIsGuest = useSelector(selectIsUserGuest);

  const openModal = (formName: string) => {
    dispatch(doOpenModal({type: ModalType.Form, formName}))
  }

  return (
    <div className={styles.channelMenuBar}>
      <button className={styles.menuButton} onClick={() => openModal("Search")}><SearchIcon /> Search</button>
      {!userIsGuest ? <button className={styles.menuButton} onClick={() => openModal("Favorites")}><FavoritesIcon /> Favorites</button> : ''}
      <button className={styles.menuButton} onClick={() => openModal("ChannelSettings")}><GoSettings /> Settings</button>
    </div>
  )
}

function ChannelColumnControl({numCols, curCol, panToCol, visible}: {numCols: number, curCol: number, panToCol: Function, visible: boolean}) {
  if (!visible) {
    return null;
  }

  return (
    <div className={styles.channelColumnControl}>
      {curCol-1 >= 0 ? <TiArrowLeftThick className={styles.channelColumnControlLeft} onClick={() => panToCol(curCol-1)} /> : ''}
      {curCol+1 < numCols ? <TiArrowRightThick className={styles.channelColumnControlRight} onClick={() => panToCol(curCol+1)} /> : ''}
    </div>
  )
}

const mapState = (state: RootState) => {
  const layoutData = selectLayoutData(state);

  return {
    channelId: layoutData.boardId,
    channelHeight: layoutData.channelHeight,
    channelWidth: layoutData.channelWidth,
    centroidId: layoutData.centroidCellId,
    channelName: selectCurrentChannel(state)?.name,
    focusedCell: state.channelLayout.focusedCell,
    pannedCell: state.channelLayout.pannedCell,
    theme: state.system.theme,
    layoutLoading: state.channelLayout.fetchStatus.loading,
    layoutLoaded: state.channelLayout.layoutLoaded,
    layoutError: state.channelLayout.fetchStatus.userMessage,
    layoutLastFetch: state.channelLayout.fetchStatus.lastFetch,
    mobileMenuOpen: selectMobileMenuOpen(state),
    desktopDrawerOpen: selectDesktopDrawerOpen(state),
    layoutType: selectLayoutType(state),
    layoutColumnDims: selectLayoutColumnDims(state),
    windowWidth: state.system.windowDims.w
  }
}

const mapDispatch = {
  doBlinkCell,
  doUnfocusCell,
  doStopPanningToCell,
  doSetLayoutLoaded,
  doSetMobileMenuOpen
}

const connector = connect(
  mapState,
  mapDispatch,
  null,
  { forwardRef: true }
)

type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(Channel);
